<script setup lang="ts">
import { bemBuilder, getQueryParamValue } from "@chatfood/core-utils";
import { isMenuRepoError } from "@chatfood/dbe-client/errors";
import { SatElasticPanel, useToast } from "@chatfood/design-system";
import {
  BASE_TELEPORT_SELECTOR,
  ref,
  watch,
  useAnalytics,
  useBugReporter,
  computed,
  useFeatureFlag,
  onBeforeMount,
} from "#imports";
import { useNavigation } from "~/composables/use-navigation";
import { useBusinessStore } from "~/stores/business";
import { useItemStore } from "~/stores/item";
import { useOutletStore } from "~/stores/outlet";
import { useRichCard } from "~/composables/use-rich-card";
import { useLocale } from "~/composables/use-locale";
import { ItemView } from "~/components/item-view";
import { ItemBuilder } from "~/components/item-builder";

const css = bemBuilder("item-modal");

const props = defineProps<{
  menuId: string;
}>();

const business = useBusinessStore();
const outlet = useOutletStore();
const itemStore = useItemStore();

const { t } = useLocale();
const { rawPush, route } = useNavigation();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { toastError, toastUnexpectedError } = useToast();
const { isEnabled } = useFeatureFlag();

const isVisible = ref(false);

const itemId = computed<string | undefined>(() => {
  return getQueryParamValue(route.value.query, "itemId");
});

const onClose = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { itemId, ...otherQuery } = route.value.query;

  rawPush({
    query: otherQuery,
  });

  itemStore.reset();
};

const loadItem = async () => {
  if (!itemId.value || !outlet.id) {
    toastError(t("modals.item.missing_base_properties"));
    onClose();
    return;
  }

  try {
    if (itemId.value !== itemStore.id) {
      await itemStore.load(itemId.value, outlet.id);
    }

    const title = t("modals.item.seo_title", {
      itemName: itemStore.name ?? "",
      businessName: business.name,
    });

    const description =
      itemStore.description ||
      t("modals.item.seo_description", {
        businessName: business.name,
      });

    const image = itemStore.photo || business.cover || business.logo;
    useRichCard({ title, description, image });

    trackEvent({
      name: "viewItem",
      payload: {
        id: itemStore.id,
        name: itemStore.name,
        price: itemStore.price,
      },
    });
  } catch (error: unknown) {
    if (isMenuRepoError(error) && !error.isNotFound()) {
      toastError(t("modals.item.item_not_found"));
      onClose();
      return;
    }

    toastUnexpectedError();
    report(error);
    onClose();
  }
};

watch(
  itemId,
  (value) => {
    isVisible.value = Boolean(value);
  },
  { immediate: true },
);

watch(
  isVisible,
  (value) => {
    if (value) {
      loadItem();
    } else {
      onClose();
    }
  },
  { immediate: true },
);

const isNewItemFlow = ref(false);

onBeforeMount(async () => {
  isNewItemFlow.value = await isEnabled("direct.useNewItemFlow");
});
</script>

<template>
  <Teleport :to="BASE_TELEPORT_SELECTOR">
    <SatElasticPanel
      v-model="isVisible"
      :class="css()"
      with-transparent-fade
      full-height
      show-close
      width-fit-content
      padding="none"
      h-position="center"
      :inner-scroll="true"
    >
      <ItemBuilder
        v-if="isNewItemFlow"
        :menu-id="props.menuId"
        @close="onClose"
      />
      <ItemView v-else :menu-id="props.menuId" @close="onClose" />
    </SatElasticPanel>
  </Teleport>
</template>

<style lang="scss" scoped>
.item-modal {
  :deep(.mol-modal__card) {
    overflow: auto;
  }
}
</style>
